import React from 'react'
import type { PartialContent, PartialMoment } from './momentTypes'

type Service = { id: string; name?: string; systemName?: string }

// The full set of callbacks `helpers.js:buildContextMenuCallbacks` always
// produces. Sharing one canonical type so the same `contextMenuCallbacks`
// instance is consumed by both moment-side (`MomentCardBody`,
// `MomentCardFooter`) and content-side (`ContentCardContextMenu`) without
// the two consumers defining disjoint types that fail to overlap.
export type ContextMenuCallbacks = {
  changeDaySegment: (momentId: string, newDaySegment: string) => void
  deleteContent: (id: string, moment: PartialMoment | undefined) => void
  deleteMoment: (momentId: string, withConfirmation?: boolean) => void
  duplicateContent: (
    content: PartialContent,
    moment: PartialMoment | undefined,
    pivotName?: string
  ) => void
  duplicateMoment: (momentId: string) => void
  renameContent: (contentId: string, newName: string) => void
  renameMoment: (momentId: string, newName: string) => void
  onAddToBoard: (...args: unknown[]) => void
  onAddToPlan: (...args: unknown[]) => void
  pickMomentCoverAsset: (moment: PartialMoment) => void
}

export type CardContextValue = {
  cancelMoment: () => void
  contextMenuCallbacks: ContextMenuCallbacks
  createMoment: (
    moment: Record<string, unknown>,
    selectedServices?: Array<Service>,
    options?: { applyNameToContent?: boolean; contentScheduledAt?: string }
  ) => Promise<unknown> | void
  // Used by Content mode's "attach to existing moment" flow. Two-step:
  // create the post under the existing moment, then patch it with the
  // cell-derived schedule (`useCreateContent` doesn't accept
  // `scheduled_at`). Optional because only Content mode wires it.
  createContentForMoment?: (args: {
    momentId: string
    service: Service
    name?: string
    scheduled_at?: string
    takedown_scheduled_at?: string | null
  }) => Promise<unknown>
  errors: unknown
  isDraggingContent: boolean
  navigateTo: (arg: unknown) => void
  onDismissError: (...args: unknown[]) => void
  onMoveContent: (arg: unknown) => void
  onReorderContent: (arg: unknown) => void
  onRescheduleContent?: (args: {
    contentId: string
    scheduled_at: string
    takedown_scheduled_at?: string | null
    // Optional — only the Histogram bar drop path passes it, so the
    // Histogram's local `rescheduleContent` wrapper can patch its own
    // bar counts immediately (mirroring the moment side's
    // `updateMoment` → `updateHistogramData` optimistic shift). The
    // underlying `WeekView/index.js:onRescheduleContent` callback
    // ignores the field.
    previousScheduledAt?: string
  }) => void
  requestAccess: (arg: unknown) => void
  setDragOrigin: (arg: Record<string, unknown>) => void
  setIsDraggingContent: (arg: boolean) => void
  story: Record<string, unknown>
}

const CardContext = React.createContext<CardContextValue | undefined>(undefined)

export default CardContext
